import React from "react";
import { telemetryService } from "../../../services/TelemetryService";
import DashboardCards from "./DashboardCards";
import { cache } from "../Cache";

import { useTheme, Box, CircularProgress } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

const styles = (theme) => ({
    root: {
        margin: "auto",
        width: "93%",
        flexGrow: 1,
        [theme.breakpoints.down("lg")]: {
            width: "95%",
        },
    },
    spinnerContainer: {
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    cardsContainer: {
        marginTop: "1rem",
    },
    togglebutton: {
        border: "2px solid rgba(0, 0, 0, 0.12)",
    },
});

class DeviceHealthMain extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            loading: true,
            edgeMessages: null,
            value: 0,
            lineType: "value",
            selectedIds: [],
            filteredMessages: [],
            offlineDevices: [],
            searchGroup: null,
        };

        this.requestFunctions = [this.getEdgeMessages];

        this.tabs = {
            1: "edgeMessages",
        };

        this.deviceTypes = {
            0: "v3",
        };

        this.deviceIds = {
            0: "edge_device_id",
        };

        this.searchGroupMap = {
            edgeDeviceId: "edge_device_id",
            companyName: "client_name",
            lineName: "line_name",
        };

        this.classes = styles(this.props.theme);

        this.pageDeviceType = this.props.location.state || { deviceType: "v3" };
    }

    async componentDidMount() {
        window.scrollTo(0, 0);

        let type = this.deviceTypes[this.state.value];

        Promise.all([
            telemetryService.getAllEdgeMessages(type),
            telemetryService.getDeviceHealth("v3", "NOT OK", true),
        ]).then((responses) => {
            this.formatEdgeMessages(responses[0]);
            this.setState({ offlineDevices: responses[1], loading: false });
        });
    }

    async componentDidUpdate(_, prevState) {
        if (prevState.value !== this.state.value) {
            this.setState({ loading: true });

            await this.requestFunctions[this.state.value]();

            this.setState({ loading: false });
        } else if (prevState.selectedIds !== this.state.selectedIds) {
            if (this.state.selectedIds?.length && this.state.searchGroup) {
                let selectedMessages = [];
                let currentMessages = this.tabs[this.state.value + 1];
                let insertedMessages = {};

                this.state.selectedIds.forEach((s) => {
                    this.state[currentMessages].forEach((e) => {
                        let key = this.searchGroupMap[this.state.searchGroup];
                        let value = e[0][key];
                        let idType = this.deviceIds[this.state.value];
                        let deviceId = e[0][idType];

                        if (value === s && insertedMessages[deviceId] === undefined) {
                            insertedMessages[deviceId] = true;
                            selectedMessages.push(e);
                        } else if (
                            Array.isArray(value) &&
                            value.find((v) => v.includes(s)) &&
                            insertedMessages[deviceId] === undefined
                        ) {
                            insertedMessages[deviceId] = true;
                            selectedMessages.push(e);
                        }
                    });
                });

                this.setState({ filteredMessages: selectedMessages });
            } else {
                this.setState({ filteredMessages: [] });
            }
        } else if (this.props.location.hash) {
            let searchType = this.props.location.hash.split("#")[1];
            let currentType = this.deviceTypes[this.state.value];
            let searchTypeValue = Object.keys(this.deviceTypes).find((key) => this.deviceTypes[key] === searchType);
            if (searchType !== currentType) {
                this.props.navigate({
                    hash: "",
                });

                this.setState({ value: Number(searchTypeValue) });
            }
        }
    }

    fetchOfflineDevices = async () => {
        let type = this.deviceTypes[this.state.value];
        let devices = await telemetryService.getDeviceHealth(type, "NOT OK", true);

        this.setState({ offlineDevices: devices });
    };

    formatEdgeMessages = (result) => {
        if (result?.length) {
            let messages = [];
            let deviceIdsMap = {};

            result.forEach((r) => {
                let id = r.edge_device_id;
                let message = messages.find((m) => Number(Object.keys(m)[0]) === Number(id));

                deviceIdsMap[id] = {
                    edgeDeviceId: id.toString(),
                    companyName: r.client_name,
                    lineName: r.line_name,
                };

                if (message) {
                    let valuesArr = Object.values(message)[0];

                    valuesArr.push(r);
                } else {
                    messages.push({ [id]: [r] });
                }
            });

            let formattedMessages = messages.map((m) => {
                let key = Object.keys(m)[0];
                return m[key];
            });

            let deviceIds = Object.values(deviceIdsMap);

            cache.set("edgeMessages", result, 5);

            this.setState({ edgeMessages: formattedMessages, deviceIds });
        }
    };

    getEdgeMessages = async () => {
        let cachedResult = cache.get("edgeMessages");
        let type = this.deviceTypes[this.state.value];

        if (cachedResult?.length) {
            this.formatEdgeMessages(cachedResult);
        } else {
            let result = await telemetryService.getAllEdgeMessages(type);

            if (result?.length) {
                this.formatEdgeMessages(result);
            }
        }

        this.setState({ loading: false });
    };


    handleChange = (_, newValue) => {
        this.setState({ filteredMessages: [], value: newValue, searchGroup: null });
    };

    handleLineType = (_, newLineType) => {
        if (newLineType && this.state.lineType !== newLineType) {
            this.setState({ lineType: newLineType });
        }
    };

    onTagsChange = (_, searchGroup, values) => {
        this.setState({ searchGroup, selectedIds: values });
    };

    updateOfflineDevices = (deviceId, note) => {
        let offDevices = this.state.offlineDevices;
        let idx = offDevices.findIndex((device) => device.edge_device_id === deviceId);
        let updatedDevice = offDevices[idx];
        updatedDevice.note = note;

        this.setState({ offlineDevices: offDevices });
    };

    render() {
        let {
            lineType,
            value,
            loading,
            edgeMessages,
            filteredMessages,
            deviceIds,
            offlineDevices,
            searchGroup,
        } = this.state;

        return (
            <Box component="div" sx={this.classes.root}>
                {loading && (
                    <Box component="div" sx={this.classes.spinnerContainer}>
                        <CircularProgress size={"4rem"} />
                    </Box>
                )}

                <Box component="div" sx={this.classes.cardsContainer}>
                    <DashboardCards
                        lineType={lineType}
                        edgeMessages={edgeMessages}
                        filteredMessages={filteredMessages}
                        value={value}
                        loading={loading}
                        offlineDevices={offlineDevices}
                        deviceIds={deviceIds}
                        onTagsChange={this.onTagsChange}
                        handleLineType={this.handleLineType}
                        searchGroup={searchGroup}
                        fetchOfflineDevices={this.fetchOfflineDevices}
                        updateOfflineDevices={this.updateOfflineDevices}
                    />
                </Box>
            </Box>
        );
    }
}

const DeviceHealthMainWrapper = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();

    return <DeviceHealthMain location={location} navigate={navigate} theme={theme} />;
}

export default DeviceHealthMainWrapper;
